// packages/web/src/lib/auth/logout.ts
// FAANG-GRADE: Unified logout utility
// Single source of truth for logout logic across all components

import { apiFetch } from '../api';
import { clearAllAuthData } from './session-manager';

export interface LogoutOptions {
  redirectTo?: string;
  skipApiCall?: boolean;
}

/**
 * FAANG-GRADE: Unified logout function
 * Handles API call, storage cleanup, and full-page navigation
 *
 * IMPORTANT: Order of operations matters here:
 * 1. Call logout API (while token is still valid)
 * 2. Clear session cache + all storage (localStorage, sessionStorage, cookies)
 * 3. Navigate away (full page load creates fresh Redux store from empty storage)
 *
 * KEY DESIGN DECISION: We do NOT dispatch Redux actions (reduxLogout, clearTenant)
 * or custom events (cartUpdated, authStateChanged) before navigation.
 *
 * Why: Dispatching Redux actions triggers React re-renders while the page is
 * navigating away. Components like MarketplaceContainer re-render, their useEffects
 * fire, new fetch requests start, but the browser navigation aborts them all,
 * causing "Failed to fetch" errors that trip the circuit breaker.
 *
 * Since we do a full page load (window.location.href), the new page creates a
 * fresh Redux store that hydrates from localStorage (which is already cleared).
 * No Redux cleanup is needed.
 */
export async function logout(options: LogoutOptions = {}): Promise<void> {
  // Detect catalog context for default redirect
  let defaultRedirect = '/'; // Default to current domain root
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/c/')) {
      const catalogMatch = currentPath.match(/^\/c\/([^/?]+)/);
      if (catalogMatch) {
        defaultRedirect = `/c/${catalogMatch[1]}?logged_out=1`;
      }
    } else {
      // For main marketplace, redirect to root with logged out indicator
      defaultRedirect = '/?logged_out=1';
    }
  }

  const { redirectTo = defaultRedirect, skipApiCall = false } = options;

  console.log('[LOGOUT] Starting unified logout process');

  try {
    // Step 1: Call logout API (while token is still valid)
    if (!skipApiCall) {
      try {
        await apiFetch('/auth/logout', { method: 'POST' });
      } catch (error) {
        console.warn('[LOGOUT] Logout API call failed, proceeding with client cleanup:', error);
      }
    }

    // Step 2: Clear ALL auth data including session cache, localStorage,
    // sessionStorage, and cookies. This is the only cleanup needed before
    // navigation — the full page reload creates a fresh app state.
    clearAllAuthData();

    // Step 3: Navigate immediately — do NOT dispatch Redux actions first.
    // Full page navigation creates a fresh Redux store from (now-cleared) storage.
    // Dispatching before navigation would trigger re-renders → fetch calls → aborted
    // requests → circuit breaker trips.
    if (typeof window !== 'undefined') {
      window.location.href = redirectTo;
    }

    console.log('[LOGOUT] Logout process completed');
  } catch (error) {
    console.error('[LOGOUT] Critical error during logout process:', error);

    // Fallback: at least clear auth data and force-navigate
    try {
      clearAllAuthData();
      if (typeof window !== 'undefined') {
        window.location.href = redirectTo;
      }
    } catch (fallbackError) {
      console.error('[LOGOUT] Fallback cleanup also failed:', fallbackError);
    }

    throw error;
  }
}

/**
 * Convenience function for component use
 */
export function useLogout() {
  return { logout };
}
